import { BiReportsTracking } from "@/lib/tracking/biReports";
import { FlexListSummary } from "@/components/listSummary";
import { FilterTagWrapper } from "@/components/tags";
import { Button, Card, CardContent, CardHeader, Typography } from "@altimateai/lego";
import BIUserReportInsightOrOpportunityTag, {
  opportunityToTagsTooltipDisplayFormat,
} from "./BIUserReportInsightOrOpportunityTag";
import { DataTable } from "@/components/tables";
import { BiUserReport, GetUserReportsParams } from "./types";
import tagStyles from "@/components/tags/styles.module.scss";
import { useQuery } from "@tanstack/react-query";
import {
  GET_BI_USER_REPORTS,
  GET_BI_USER_REPORTS_FILTERS,
  GET_BI_USER_SUMMARY,
} from "@/pages/query/constants";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import {
  downloadBiUserReportsCSV,
  getUserReports,
  getUserReportsFilters,
  getUserSummary,
} from "@/helpers/apis";
import { Stack } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import { FaUser } from "react-icons/fa";
import { FilterApplicableFor } from "@/components/tags/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useSearchParams, Link } from "react-router-dom";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { useEffect, useState } from "react";
import usePaginate from "@/components/paginate/usePaginate";
import { Paginate } from "@/components/paginate";
import { DownloadTable } from "@/components/downloadTable";
import SavedFilters from "@/components/tags/SavedFilters";
import { DateRange, StaticDateRangeKeys } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";

// Search parameter constants matching the API
const SEARCH_PARAMS = {
  PAGE: "page",
  PAGE_SIZE: "page_size",
  USER: "user",
  LICENSE_TYPE: "license_type",
  INSIGHTS: "insights",
  INSTANCE_NAME: "instance_name",
  OPPORTUNITY_NAME: "opportunity_name",
  SORT_BY: "sort_by",
  SORT: "sort",
  START_DATE: "start_date",
  END_DATE: "end_date",
} as const;

const BiUserReportsList = () => {
  useBreadcrumb("Users", {
    defaultParents: [{ label: "Insights", pathname: "/bi-reports", noLink: true }],
    truncateTo: "/bi-reports",
  });
  const presetFilters = [
    {
      label: "Optimize License Costs",
      values: [
        {
          insightsFilter: [
            "downgrade_creator_to_explorer",
            "downgrade_explorer_to_viewer",
            "remove_license_inactive",
          ],
        },
      ],
    },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();
  // Date range state
  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(90, "day").toDate(),
    dayjs().toDate()
  );

  // Filter states - matching API parameters
  const [userFilter, setUserFilter] = useState<string[]>(
    searchParams.getAll(SEARCH_PARAMS.USER) || []
  );
  const [licenseTypeFilter, setLicenseTypeFilter] = useState<string[]>(
    searchParams.getAll(SEARCH_PARAMS.LICENSE_TYPE) || []
  );
  const [insightsFilter, setInsightsFilter] = useState<string[]>(
    searchParams.getAll(SEARCH_PARAMS.INSIGHTS) || []
  );
  const [instanceNameFilter, setInstanceNameFilter] = useState<string[]>(
    searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || []
  );
  const [opportunityNameFilter, setOpportunityNameFilter] = useState<string[]>(
    searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME) || []
  );

  // Sort states
  const sortBy = searchParams.get(SEARCH_PARAMS.SORT_BY) || "email";
  const sortOrder = parseSortDirection(searchParams.get(SEARCH_PARAMS.SORT), "asc");

  // Initialize filters and pagination from URL params
  useEffect(() => {
    setUserFilter(searchParams.getAll(SEARCH_PARAMS.USER) || []);
    setLicenseTypeFilter(searchParams.getAll(SEARCH_PARAMS.LICENSE_TYPE) || []);
    setInsightsFilter(searchParams.getAll(SEARCH_PARAMS.INSIGHTS) || []);
    setInstanceNameFilter(searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || []);
    setOpportunityNameFilter(searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME) || []);

    // Initialize pagination from URL params
    const urlPage = searchParams.get(SEARCH_PARAMS.PAGE);
    const urlPageSize = searchParams.get(SEARCH_PARAMS.PAGE_SIZE);

    if (urlPage) {
      const pageNum = parseInt(urlPage) - 1; // Convert to 0-based index
      if (pageNum >= 0 && pageNum !== page) {
        setPage(pageNum);
      }
    }

    if (urlPageSize) {
      const size = parseInt(urlPageSize);
      if (size > 0 && size !== pageSize) {
        updatePageSize(size);
      }
    }
  }, [searchParams]);

  const handlePreSelect = (values: Array<{ [key: string]: string[] }>) => {
    // Convert the values array to a flat object for search params
    const flatValues = values.reduce(
      (acc, value) => {
        const entries = Object.entries(value);
        entries.forEach(([key, val]) => {
          // Map the filter names to their corresponding search parameter names
          switch (key) {
            case "insightsFilter":
              acc[SEARCH_PARAMS.INSIGHTS] = val;
              break;
            case "licenseTypeFilter":
              acc[SEARCH_PARAMS.LICENSE_TYPE] = val;
              break;
            case "instanceNameFilter":
              acc[SEARCH_PARAMS.INSTANCE_NAME] = val;
              break;
            case "opportunityNameFilter":
              acc[SEARCH_PARAMS.OPPORTUNITY_NAME] = val;
              break;
            default:
              acc[key] = val;
          }
        });
        return acc;
      },
      {} as { [key: string]: string[] }
    );

    // Clear all params and set only the new preset values
    // Use setSearchParams directly to avoid race condition between clearSearchParams and updateSearchParamsByKey
    const params = new URLSearchParams();
    Object.entries(flatValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(val => params.append(key, val));
      } else {
        params.set(key, value);
      }
    });

    // Set params directly, replacing all previous params
    setSearchParams(params, { replace: true });
    resetPage();
  };

  const isSelected = (values: Array<{ [key: string]: string[] }>) => {
    const valuesMap = new Map<string, string[]>();

    values.forEach(value => {
      Object.entries(value).forEach(([key, val]) => {
        // Map the filter names to their corresponding search parameter names
        let searchParamKey = key;
        switch (key) {
          case "insightsFilter":
            searchParamKey = SEARCH_PARAMS.INSIGHTS;
            break;
          case "licenseTypeFilter":
            searchParamKey = SEARCH_PARAMS.LICENSE_TYPE;
            break;
          case "instanceNameFilter":
            searchParamKey = SEARCH_PARAMS.INSTANCE_NAME;
            break;
          case "opportunityNameFilter":
            searchParamKey = SEARCH_PARAMS.OPPORTUNITY_NAME;
            break;
        }

        if (!valuesMap.has(searchParamKey)) {
          valuesMap.set(searchParamKey, []);
        }
        valuesMap.get(searchParamKey)!.push(...val.map(String));
      });
    });

    // Check if all expected parameters match
    for (const [key, expectedValues] of valuesMap.entries()) {
      const currentValues = searchParams.getAll(key);
      if (currentValues.length !== expectedValues.length) {
        return false;
      }
      if (!expectedValues.every(val => currentValues.includes(val))) {
        return false;
      }
    }

    // Check if there are no extra parameters (ignoring pagination and other non-filter params)
    const ignoredParams = ["page", "page_size", "sort_by", "sort", "start_date", "end_date"];
    const currentFilterParams = Array.from(searchParams.keys()).filter(
      key => !ignoredParams.includes(key)
    );

    if (currentFilterParams.length !== valuesMap.size) {
      return false;
    }

    return true;
  };

  const handleArrayFilterChange = (setFilter: (value: string[]) => void, param: string) => {
    return (value: string[]) => {
      updateSearchParamsByKey({ [param]: value, [SEARCH_PARAMS.PAGE]: "1" });
      setFilter(value);
      resetPage();
    };
  };

  // Filter change handlers
  const handleUserFilterChange = handleArrayFilterChange(setUserFilter, SEARCH_PARAMS.USER);
  const handleLicenseTypeFilterChange = handleArrayFilterChange(
    setLicenseTypeFilter,
    SEARCH_PARAMS.LICENSE_TYPE
  );
  const handleInsightsFilterChange = handleArrayFilterChange(
    setInsightsFilter,
    SEARCH_PARAMS.INSIGHTS
  );
  const handleInstanceNameFilterChange = handleArrayFilterChange(
    setInstanceNameFilter,
    SEARCH_PARAMS.INSTANCE_NAME
  );
  const handleOpportunityNameFilterChange = handleArrayFilterChange(
    setOpportunityNameFilter,
    SEARCH_PARAMS.OPPORTUNITY_NAME
  );

  const getFilterParams = () => {
    const params: GetUserReportsParams = {
      page: page + 1,
      page_size: pageSize,
      sort_by: sortBy,
      sort: sortOrder,
    };

    // Add date range parameters
    if (startDate) {
      const startDateISO = getDateByStartOfDayWithoutTimeoffset(startDate)?.toISOString();
      if (startDateISO) params.start_date = startDateISO;
    }
    if (endDate) {
      const endDateISO = getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString();
      if (endDateISO) params.end_date = endDateISO;
    }

    if (userFilter.length > 0) params.user = userFilter;
    if (licenseTypeFilter.length > 0) params.license_type = licenseTypeFilter;
    if (insightsFilter.length > 0) params.insights = insightsFilter;
    if (instanceNameFilter.length > 0) params.instance_name = instanceNameFilter;
    if (opportunityNameFilter.length > 0) params.opportunity_name = opportunityNameFilter;

    return params;
  };

  const { data: userReportsData, isFetching: isLoading } = useQuery({
    queryKey: [
      GET_BI_USER_REPORTS,
      page,
      pageSize,
      sortBy,
      sortOrder,
      startDate,
      endDate,
      ...userFilter,
      ...licenseTypeFilter,
      ...insightsFilter,
      ...instanceNameFilter,
      ...opportunityNameFilter,
    ],
    queryFn: () => getUserReports(getFilterParams()),
  });

  const { data: userSummary, isFetching: isUserSummaryLoading } = useQuery({
    queryKey: [GET_BI_USER_SUMMARY],
    queryFn: () => getUserSummary(),
  });

  const { data: filters, isFetching: isFiltersLoading } = useQuery({
    queryKey: [GET_BI_USER_REPORTS_FILTERS],
    queryFn: () => getUserReportsFilters(),
  });

  const getOptions = (options: string[] | undefined) =>
    options
      ? options.map(o => (o === "" ? { label: "None", value: o } : { label: o, value: o }))
      : [];

  const handleFilterRemoval = (
    filter: string[],
    setFilter: (value: string[]) => void,
    param: string
  ) => {
    return (removedFilter?: string) => {
      const newFilter = filter.filter(f => f !== removedFilter);
      deleteSearchParamsByKeyValue({ [param]: removedFilter });
      setFilter(newFilter);
    };
  };

  const handleDownload = async () => {
    const downloadParams = getFilterParams();
    // Remove pagination params for download to get all data
    const { page: _page, page_size: _page_size, ...filterParams } = downloadParams;
    await downloadBiUserReportsCSV(filterParams);
  };

  const headers: TableHeaderType<BiUserReport>[] = [
    {
      label: "User",
      uid: "email",
      render: item => (
        <Link
          to={
            !item.user_rk
              ? "/bi-reports/users"
              : `/bi-reports/users/${encodeURIComponent(item.user_rk)}`
          }
          state={{ entityName: item.email }}
          className="al-text-brand al-break-all cursor-pointer"
          onClick={() => item.user_rk && BiReportsTracking.userReportViewed(item.user_rk)}
        >
          <Stack gap={"sm"} className={"al-text-brand al-cursor-pointer hover:al-opacity-80"}>
            <FaUser />
            <Stack direction="column" gap="xs">
              <span className="al-text-xs al-font-normal al-break-all">{item.email}</span>
              {item.full_name && (
                <span className="al-text-xs al-text-gray-600 al-font-normal">{item.full_name}</span>
              )}
            </Stack>
          </Stack>
        </Link>
      ),
      id: 0,
      width: 250,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: userFilter,
        options: getOptions(filters?.user),
        onChange: (value: string[]) => handleUserFilterChange(value),
      },
    },
    {
      label: "License",
      uid: "current_license_type",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">
          {item.current_license_type || "Unknown"}
        </span>
      ),
      id: 1,
      width: 250,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: licenseTypeFilter,
        options: getOptions(filters?.license_type),
        onChange: (value: string[]) => handleLicenseTypeFilterChange(value),
      },
    },
    {
      label: "Insights",
      uid: "insights",
      width: 300,
      render: item => (
        <div className={tagStyles.tags}>
          {item.insights.map((insight, index) => (
            <BIUserReportInsightOrOpportunityTag key={index} insightOrOpportunity={insight} />
          ))}
        </div>
      ),
      id: 2,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: insightsFilter,
        options: getOptions(filters?.insights),
        onChange: (value: string[]) => handleInsightsFilterChange(value),
      },
    },
    {
      label: "Opportunities",
      uid: "opportunities",
      width: 250,
      tooltipInfo: "Cost saving opportunities for this user",
      render: item => (
        <div className={tagStyles.tags}>
          {(item?.opportunities || []).map(opportunity => (
            <BIUserReportInsightOrOpportunityTag
              key={opportunity.opportunity_id}
              insightOrOpportunity={opportunityToTagsTooltipDisplayFormat(opportunity)}
            />
          ))}
        </div>
      ),
      id: 3,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: opportunityNameFilter,
        options: filters?.opportunity_name
          ? filters.opportunity_name.map((opportunityName: string) => ({
              label: opportunityName
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase()),
              value: opportunityName,
            }))
          : [],
        onChange: (value: string[]) => handleOpportunityNameFilterChange(value),
      },
    },
    {
      label: `Views`,
      tooltipInfo: "Total dashboard views by this user across all Tableau content",
      uid: "views",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.views}</span>
      ),
      id: 4,
      sort: {
        sortSearchKey: SEARCH_PARAMS.SORT_BY,
        sortOrderSearchKey: SEARCH_PARAMS.SORT,
      },
    },
    {
      label: "Dashboard Created",
      tooltipInfo: "Total dashboards created by this user in the activity period",
      uid: "dashboards_created",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">
          {item.dashboards_created}
        </span>
      ),
      id: 5,
      sort: {
        sortSearchKey: SEARCH_PARAMS.SORT_BY,
        sortOrderSearchKey: SEARCH_PARAMS.SORT,
      },
    },
    {
      label: "Dashboard Consumption",
      tooltipInfo:
        "Total views received on this user's dashboards from other users (excluding the creator's own views) in the activity period",
      uid: "views_received",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.views_received}</span>
      ),
      id: 6,
      sort: {
        sortSearchKey: SEARCH_PARAMS.SORT_BY,
        sortOrderSearchKey: SEARCH_PARAMS.SORT,
      },
    },
    {
      label: "Instance Name",
      uid: "instance_name",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">
          {item.instance_name || "Unknown"}
        </span>
      ),
      id: 7,
      width: 150,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: instanceNameFilter,
        options: getOptions(filters?.instance_name),
        onChange: (value: string[]) => handleInstanceNameFilterChange(value),
      },
    },
    {
      label: "Last Activity",
      uid: "last_activity",
      tooltipInfo:
        "Most recent dashboard view or creation activity by this user. 'Never' indicates no activity found in our records.",
      render: item => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">
          {item.last_activity ? new Date(item.last_activity).toLocaleDateString() : "Never"}
        </span>
      ),
      id: 8,
      width: 120,
      sort: {
        sortSearchKey: SEARCH_PARAMS.SORT_BY,
        sortOrderSearchKey: SEARCH_PARAMS.SORT,
      },
    },
  ];

  if (isLoading || isFiltersLoading) {
    return (
      <div>
        {!isUserSummaryLoading && (
          <FlexListSummary
            summary={(userSummary || []).map(item => ({ ...item, value: String(item.value) }))}
          />
        )}
        <div className="al-mt-4">
          <Card>
            <CardContent>
              <Stack align={"center"} justify={"center"} className={"al-w-full al-min-h-72"}>
                <RelativeComponentLoader label={"Loading..."} />
              </Stack>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!isUserSummaryLoading && (
        <FlexListSummary
          summary={(userSummary || []).map(item => ({ ...item, value: String(item.value) }))}
        />
      )}

      <Stack gap={"md"} align={"center"} className="al-mb-2 al-mt-4">
        <FilterTagWrapper
          applicableFor={FilterApplicableFor.BI_REPORTS_USERS}
          filters={{
            userFilter: {
              filterStr: userFilter,
              label: "User",
              onclose: handleFilterRemoval(userFilter, setUserFilter, SEARCH_PARAMS.USER),
              searchKey: SEARCH_PARAMS.USER,
              bgColor: "white",
            },
            licenseTypeFilter: {
              filterStr: licenseTypeFilter,
              label: "License Type",
              onclose: handleFilterRemoval(
                licenseTypeFilter,
                setLicenseTypeFilter,
                SEARCH_PARAMS.LICENSE_TYPE
              ),
              searchKey: SEARCH_PARAMS.LICENSE_TYPE,
              bgColor: "white",
            },
            insightsFilter: {
              filterStr: insightsFilter,
              label: "Insights",
              onclose: handleFilterRemoval(
                insightsFilter,
                setInsightsFilter,
                SEARCH_PARAMS.INSIGHTS
              ),
              searchKey: SEARCH_PARAMS.INSIGHTS,
              bgColor: "white",
            },
            instanceNameFilter: {
              filterStr: instanceNameFilter,
              label: "Instance Name",
              onclose: handleFilterRemoval(
                instanceNameFilter,
                setInstanceNameFilter,
                SEARCH_PARAMS.INSTANCE_NAME
              ),
              searchKey: SEARCH_PARAMS.INSTANCE_NAME,
              bgColor: "white",
            },
            opportunityNameFilter: {
              filterStr: opportunityNameFilter,
              label: "Opportunity",
              onclose: handleFilterRemoval(
                opportunityNameFilter,
                setOpportunityNameFilter,
                SEARCH_PARAMS.OPPORTUNITY_NAME
              ),
              searchKey: SEARCH_PARAMS.OPPORTUNITY_NAME,
              bgColor: "white",
            },
          }}
        />
      </Stack>

      <div className="al-mt-4">
        <Card>
          <CardHeader>
            <Stack direction={"row"} justify={"between"}>
              <Stack direction={"column"}>
                <Typography variant="h4" className="al-text-lg al-font-semibold al-text-gray-600">
                  User Activity
                </Typography>
                <Typography variant="body" className="al-text-sm al-text-gray-600">
                  Analyze user engagement patterns and license cost optimization opportunities
                </Typography>
              </Stack>
              <Stack align={"center"} gap={"md"}>
                <DateRange
                  startDate={startDate}
                  endDate={endDate}
                  availableStaticRanges={[
                    StaticDateRangeKeys.LAST_30_DAYS,
                    StaticDateRangeKeys.LAST_3_MONTHS,
                    StaticDateRangeKeys.LAST_6_MONTHS,
                  ]}
                  disableCalendarSelection
                  onDateRangeSelect={(s, e) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.START_DATE]: s?.toISOString(),
                      [SEARCH_PARAMS.END_DATE]: e?.toISOString(),
                    });
                  }}
                />
                {presetFilters.map((option, i) => (
                  <Button
                    className={"al-font-normal"}
                    key={i}
                    variant={isSelected(option.values) ? "default" : "secondary"}
                    onClick={() => handlePreSelect(option.values)}
                  >
                    {option.label}
                  </Button>
                ))}
                <SavedFilters applicableFor={FilterApplicableFor.BI_REPORTS_USERS} />
                <DownloadTable handleDownload={handleDownload} />
              </Stack>
            </Stack>
          </CardHeader>
          <CardContent>
            {!isLoading && userReportsData && (
              <>
                <DataTable
                  header={headers}
                  data={{
                    items: userReportsData.items,
                  }}
                  resetPage={resetPage}
                  columnPickerPortal="columnpicker-container"
                  minWidth={2050}
                  applicableFor={FilterApplicableFor.BI_REPORTS_USERS}
                  defaultSort={{ sortParamValue: "email", order: "asc" }}
                />
                <Paginate
                  itemCount={userReportsData.total}
                  page={page}
                  pageSize={pageSize}
                  numPages={userReportsData.pages}
                  onPageClick={page => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.PAGE]: `${page + 1}`,
                    });
                    setPage(page);
                  }}
                  onPageSizeChange={size => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.PAGE_SIZE]: `${size}`,
                      [SEARCH_PARAMS.PAGE]: "1",
                    });
                    updatePageSize(size);
                  }}
                  showPageSize
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BiUserReportsList;
